// Bê từ finiva-admin src/modules/auth/components/ForgotPasswordPage.tsx. Sửa: không
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@shared/utils'
import { useForgotPassword } from '../hooks/useAuth'
import { ROUTES } from '@config/routes'

const schema = z.object({
  email: z.string().email('Email không hợp lệ'),
})

type FormValues = z.infer<typeof schema>

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useForgotPassword()
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const onSubmit = async (values: FormValues) => {
    setError(null)
    try {
      await mutateAsync({ email: values.email, type: 'admin' })
      setSubmitted(true)
    } catch {
      setError(t('auth.sendError'))
    }
  }

  if (submitted) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-secondary px-4">
        <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-lg shadow-brand-900/5">
          <div className="mb-6 flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-state-ok-bg">
              <CheckCircle className="size-6 text-state-ok" />
            </div>
            <h1 className="text-lg font-semibold text-white">{t('auth.checkEmail')}</h1>
            <p className="text-sm text-muted-foreground">{t('auth.emailSentMsg')}</p>
          </div>

          <Link
            to={ROUTES.RESET_PASSWORD}
            className="block w-full rounded-lg bg-brand-500 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-brand-600"
          >
            {t('auth.resetPasswordLink')}
          </Link>
          <Link
            to={ROUTES.LOGIN}
            className="mt-3 block text-center text-sm text-muted-foreground transition-colors hover:text-brand-600"
          >
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-secondary px-4">
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-lg shadow-brand-900/5">
        <div className="mb-6">
          <h1>{t('auth.forgotTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('auth.forgotSubtitle')}</p>
        </div>

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="email">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="admin@example.com"
              className={cn(
                'h-8 w-full rounded-lg border border-input bg-background px-2 text-sm text-foreground',
                'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                errors.email && 'border-destructive focus:border-destructive focus:ring-destructive/20',
              )}
              {...register('email')}
            />
            {errors.email && <p className="text-xs text-state-critical">{errors.email.message}</p>}
          </div>

          {error && (
            <p className="rounded-lg bg-state-critical-bg px-3 py-2 text-center text-sm text-state-critical">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="mt-1 h-8 w-full rounded-lg bg-brand-500 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? t('auth.processing') : t('auth.sendRequest')}
          </button>
        </form>

        <Link
          to={ROUTES.LOGIN}
          className="mt-4 block text-center text-sm text-muted-foreground transition-colors hover:text-brand-600"
        >
          {t('auth.backToLogin')}
        </Link>
      </div>
    </div>
  )
}
